// src/hooks/useColumnPrefs.ts
//
// Per-user, per-table column visibility preferences. Stores the set of HIDDEN
// column keys in localStorage, keyed by (storageKey + user id) so each logged-in
// user gets their own layout — even on a shared workstation.
//
// Per-browser (not synced across devices). Defaults to "nothing hidden" so a
// user who never touches it sees every column.

import { useCallback, useEffect, useState } from 'react';

function load(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

export interface ColumnPrefs {
  /** True when the column is currently hidden. */
  isHidden: (colKey: string) => boolean;
  /** Show/hide a single column and persist. */
  toggle: (colKey: string) => void;
  /** Reset to defaults (show everything). */
  reset: () => void;
}

export function useColumnPrefs(storageKey: string, userId: string | null | undefined): ColumnPrefs {
  const key = `${storageKey}.${userId || 'anon'}`;
  const [hidden, setHidden] = useState<Set<string>>(() => load(key));

  // Re-load when the storage key changes (e.g. user id resolves after mount).
  useEffect(() => {
    setHidden(load(key));
  }, [key]);

  const persist = (s: Set<string>) => {
    try {
      localStorage.setItem(key, JSON.stringify([...s]));
    } catch {
      /* private mode / storage disabled — session-only is fine */
    }
  };

  const toggle = useCallback(
    (colKey: string) => {
      setHidden((cur) => {
        const next = new Set(cur);
        if (next.has(colKey)) next.delete(colKey);
        else next.add(colKey);
        persist(next);
        return next;
      });
    },
    [key],
  );

  const reset = useCallback(() => {
    setHidden(() => {
      const s = new Set<string>();
      persist(s);
      return s;
    });
  }, [key]);

  const isHidden = useCallback((colKey: string) => hidden.has(colKey), [hidden]);

  return { isHidden, toggle, reset };
}
